import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { EU_LANGUAGES, LanguageCode } from "@/lib/languages";

interface HreflangAlternate {
  lang: LanguageCode | "x-default";
  href: string; // absolute URL strongly preferred
}

interface SEOHeadProps {
  title?: string;
  description?: string;
  path?: string; // fallback only (e.g. "/pricing")
  /**
   * Provide REAL alternate URLs here (absolute preferred).
   * Example:
   * alternates={[
   *  { lang:"en", href:"https://tedmonitor.eu/multilingual-access" },
   *  { lang:"it", href:"https://tedmonitor.eu/it/accesso-multilingue" },
   *  { lang:"x-default", href:"https://tedmonitor.eu/multilingual-access" }
   * ]}
   */
  alternates?: HreflangAlternate[];
  /**
   * Set true to add noindex,nofollow meta tag (for private pages like /auth)
   */
  noIndex?: boolean;
}

const OG_LOCALE_MAP: Partial<Record<LanguageCode, string>> = {
  en: "en_US",
  it: "it_IT",
  de: "de_DE",
  fr: "fr_FR",
  es: "es_ES",
  nl: "nl_NL",
  pt: "pt_PT",
  sv: "sv_SE",
  da: "da_DK",
  fi: "fi_FI",
  el: "el_GR",
  cs: "cs_CZ",
  hu: "hu_HU",
  ro: "ro_RO",
  bg: "bg_BG",
  sk: "sk_SK",
  sl: "sl_SI",
  hr: "hr_HR",
  lt: "lt_LT",
  lv: "lv_LV",
  et: "et_EE",
  mt: "mt_MT",
  ga: "ga_IE",
  pl: "pl_PL",
};

function normalizeLang(lang: string): LanguageCode {
  const base = (lang || "en").split("-")[0] as LanguageCode;
  return (base in EU_LANGUAGES ? base : "en") as LanguageCode;
}

// Normalize pathname to avoid canonical duplicates due to trailing slashes
function normalizePathname(pathname: string) {
  if (pathname !== "/" && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

// Normalize a full URL (best effort) by removing trailing slash (except root)
function normalizeUrl(url: string) {
  try {
    const u = new URL(url);
    u.hash = "";
    u.search = "";
    u.pathname = normalizePathname(u.pathname);
    return u.toString();
  } catch {
    // If it's not a valid absolute URL, return as-is (but better to always pass absolute URLs)
    return url;
  }
}

function ensureAbsolute(siteUrl: string, href: string) {
  try {
    // If already absolute, normalize and return
    return normalizeUrl(new URL(href).toString());
  } catch {
    // If relative, resolve against siteUrl
    try {
      return normalizeUrl(new URL(href, siteUrl).toString());
    } catch {
      return href;
    }
  }
}

export default function SEOHead({ title, description, path = "", alternates = [], noIndex = false }: SEOHeadProps) {
  const { t, i18n } = useTranslation();
  const currentLang = normalizeLang(i18n.language);

  const siteUrl =
    typeof window !== "undefined" && window.location?.origin ? window.location.origin : "https://tedmonitor.eu";

  // Canonical: origin + normalized pathname only (no query, no hash)
  const canonicalUrl =
    typeof window !== "undefined" && window.location
      ? `${window.location.origin}${normalizePathname(window.location.pathname)}`
      : `${siteUrl}${path}`;

  const pageTitle = title || t("landing.title");
  const pageDescription = description || t("landing.description");

  const ogLocale = OG_LOCALE_MAP[currentLang] || "en_US";

  // Prepare alternates:
  // - normalize and make absolute
  // - dedupe by lang keeping first
  // - ensure x-default exists (fallback: canonical)
  const preparedAlternates = (() => {
    const cleaned = (alternates || [])
      .map((a) => ({
        lang: a.lang,
        href: ensureAbsolute(siteUrl, a.href),
      }))
      // drop empty href
      .filter((a) => typeof a.href === "string" && a.href.trim().length > 0);

    const seen = new Set<string>();
    const deduped: HreflangAlternate[] = [];
    for (const a of cleaned) {
      if (seen.has(a.lang)) continue;
      seen.add(a.lang);
      deduped.push(a);
    }

    // Ensure x-default exists: use English alternate if present, else canonical
    if (!seen.has("x-default")) {
      const enAlt = deduped.find((a) => a.lang === "en")?.href;
      deduped.push({ lang: "x-default", href: enAlt || normalizeUrl(canonicalUrl) });
    }

    return deduped;
  })();

  // IMPORTANT: avoid duplicating the brand if callers already pass " - TED Monitor"
  const finalTitle = (() => {
    const trimmed = (pageTitle || "").trim();
    if (!trimmed) return "TED Monitor";
    if (/ted monitor\s*$/i.test(trimmed)) return trimmed; // already ends with brand
    return `${trimmed} - TED Monitor`;
  })();

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{finalTitle}</title>
      <meta name="description" content={pageDescription} />

      {/* Robots */}
      <meta
        name="robots"
        content={
          noIndex ? "noindex,nofollow" : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1"
        }
      />

      {/* Open Graph */}
      <meta property="og:site_name" content="TED Monitor" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={normalizeUrl(canonicalUrl)} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={pageDescription} />

      {/* Canonical */}
      <link rel="canonical" href={normalizeUrl(canonicalUrl)} />

      {/* Hreflang alternates (ONLY real URLs you provide) */}
      {preparedAlternates.map(({ lang, href }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={href} />
      ))}

      {/* Structured Data (site/page baseline) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "TED Monitor",
          description: pageDescription,
          url: normalizeUrl(canonicalUrl),
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          inLanguage: currentLang,
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "EUR",
          },
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "TED Monitor",
          url: "https://tedmonitor.eu",
          logo: "https://tedmonitor.eu/logo-mark.png",
          description:
            "TED Monitor – EU public procurement monitoring platform powered by AI. Real-time alerts, CPV search, and multilingual access to Tenders Electronic Daily.",
          sameAs: [
            "https://www.linkedin.com/company/tedmonitor",
          ],
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer support",
            url: "https://tedmonitor.eu/support",
            availableLanguage: [
              "English",
              "Italian",
              "French",
              "German",
              "Spanish",
            ],
          },
        })}
      </script>
    </Helmet>
  );
}
